import { EnvGroup, EnvGroupKey } from '@flakiness/server/common/stats/envGroup.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { consume, ContextType } from '@lit/context';
import { SlSelectEvent } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { contexts } from '../contexts.js';
import { renderCategoryIcon } from '../fragments.js';
import { linkStyles } from './cssstyles.js';

function categoryFilter(category: string, droplist: Iterable<EnvGroupKey> = []) {
  return EnvGroup.open({ [EnvGroupKey.CATEGORY]: category }, droplist);
}

function isSameDroplist(group1: EnvGroup, group2: EnvGroup) {
  const droplist1 = group1.droplist();
  const droplist2 = group2.droplist();
  return droplist1.size === droplist2.size && [...droplist1].every(key => droplist2.has(key));
}

/**
 * TimelineSelector is actually a "split" selector.
 * It renders a bunch of split presets based on the given envs.
 */

@customElement('timeline-selector')
class TimelineSelector extends LitElement {
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;

  @property({ attribute: false }) split?: EnvGroup;
  // The split that "All Environments" stands for. Without it, it is the full split.
  @property({ attribute: false }) defaultSplit?: EnvGroup;
  @property({ attribute: false }) envs?: WireTypes.RunEnvironment[];

  @property({ type: Boolean }) multiple?: boolean;

  private _isSelected(split: EnvGroup) {
    return !!this.split && this.split.isFunctionallyEqual(split, this.envs ?? []) && isSameDroplist(this.split, split);
  }

  private _renderCategoryMenuItems(options: {
    categoryName: string,
    categorySplit: EnvGroup,
    selectSplit: EnvGroup,
    timelines: EnvGroup[],
  }) {
    return html`
      ${this.multiple ? html`
        <sl-menu-item .split=${options.selectSplit} type=checkbox ?checked=${this._isSelected(options.selectSplit)}>
          ${this._renderSplit(options.categorySplit)}
        </sl-menu-item>
        <sl-divider></sl-divider>
      ` : nothing}
      ${options.timelines.map(timeline => html`
        <sl-menu-item .split=${timeline} type=checkbox ?checked=${this._isSelected(timeline)}>
          ${this._renderSplit(timeline)}
        </sl-menu-item>
      `)}
    `;
  }

  private _renderCategoryMenuItem(options: {
    categoryName: string,
    categorySplit: EnvGroup,
    selectSplit: EnvGroup,
    timelines: EnvGroup[],
  }) {
    return html`
      <sl-menu-item>
        ${this._renderSplitIcon(options.categorySplit)}
        ${options.categoryName}
        ${this._renderCountBadge(options.categorySplit, 'suffix')}
        <sl-menu slot=submenu>
          ${this._renderCategoryMenuItems(options)}
        </sl-menu>
      </sl-menu-item>
    `;
  }

  render() {
    if (!this.split || !this.envs)
      return nothing;

    const allCategories = new Set<string>();
    for (const e of this.envs ?? [])
      allCategories.add(e.category);

    // "All Environments" is the default split, and categories join environments the same way.
    const allEnvironments = this.defaultSplit ?? EnvGroup.open();
    const droplist = allEnvironments.droplist();

    const categoryOptions = Array.from(allCategories)
      .sort((c1, c2) => c1.toLowerCase().localeCompare(c2.toLowerCase()))
      .map(category => ({
        categoryName: EnvGroup.categoryCollectionLabel(category),
        categorySplit: categoryFilter(category, droplist),
        selectSplit: categoryFilter(category, droplist),
        timelines: categoryFilter(category, droplist).subgroups(this.envs ?? []).sort((t1, t2) => t1.compare(t2)),
      }))
      .filter(option => option.timelines.length)
    ;

    if (categoryOptions.length === 1)
      categoryOptions[0].selectSplit = allEnvironments;

    const singleCategory = categoryOptions.length === 1 ? categoryOptions[0] : undefined;
    const displayedSplit = singleCategory && this.split.acceptsAll() ? singleCategory.categorySplit : this.split;

    return html`
      <sl-dropdown @sl-select=${(event: SlSelectEvent) => {
        const item = event.detail.item;
        if ((item as any).split) {
          this.split = (item as any).split;
          this.dispatchEvent(new CustomEvent<EnvGroup>('fk-select', { detail: this.split }));
        }
      }}>
        <sl-button slot=trigger caret>
          ${this._renderSplitIcon(displayedSplit)}
          <h-box style="gap: var(--sl-spacing-small);">
            ${this._renderSplit(displayedSplit)}
            ${this._renderCountBadge(displayedSplit)}
          </h-box>
        </sl-button>
        <sl-menu>
          ${!singleCategory && this.multiple ? html`
            <sl-menu-item .split=${allEnvironments} type=checkbox ?checked=${this._isSelected(allEnvironments)}>
              ${this._renderSplit(allEnvironments)}
            </sl-menu-item>
            <sl-divider></sl-divider>
          ` : nothing}
          ${singleCategory
            ? this._renderCategoryMenuItems(singleCategory)
            : categoryOptions.map(options => this._renderCategoryMenuItem(options))
          }
        </sl-menu>
      </sl-dropdown>
    `;
  }

  private _renderSplitIcon(split: EnvGroup) {
    if (split.acceptsAll())
      return html`<sl-icon name='list-nested' slot=prefix></sl-icon>`;;

    const categories = split.values(EnvGroupKey.CATEGORY);
    if (categories.length !== 1)
      return html`<sl-icon name=sliders slot=prefix></sl-icon>`;
    return renderCategoryIcon(categories[0], { slot: 'prefix' });
  }

  private _renderSplit(split: EnvGroup) {
    if (split.acceptsAll()) {
      return html`All Environments`;
    }

    const categories = split.values(EnvGroupKey.CATEGORY);
    if (categories.length === 1 && split.isEqual(categoryFilter(categories[0], split.droplist()))) {
      return html`All ${EnvGroup.categoryCollectionLabel(categories[0])}`;
    }

    const timelines = split.subgroups(this.envs ?? []);
    // A filter that matches none of the environments describes itself.
    const timeline = timelines.length === 1 ? timelines[0] : !timelines.length ? split : undefined;
    if (timeline) {
      const description = timeline.description();
      return html`
        <h-box style="gap: var(--sl-spacing-2x-small);">
          ${description.name ?? description.configPath}
          ${description.metadata ? `(${description.metadata})` : nothing}
          ${description.system ? html`
            <sl-icon name=dot></sl-icon>
            <span>${description.system}</span>
          ` : nothing}
          ${description.runtime ? html`
            <sl-icon name=dot></sl-icon>
            <span>${description.runtime}</span>
          ` : nothing}
          ${description.configPath ? html`<span class=timeline-path>– ${description.configPath}</span>` : nothing}
        </h-box>
      `;
    }

    return html`Custom ${timelines.length} environments`;
  }

  private _renderCountBadge(split: EnvGroup, slot?: string) {
    // Timelines, not environments: the droplist joins environments into one timeline.
    const count = split.subgroups(this.envs ?? []).length;
    if (count <= 1)
      return nothing;
    return html`<sl-tag class=count-badge slot=${slot ?? nothing} size=small pill>${count}</sl-tag>`;
  }

  static styles = [linkStyles, css`
    timeline-selector-view {
      background-color: white;
    }

    .timeline-path {
      color: var(--sl-color-neutral-500);
    }

    .count-badge::part(base) {
      height: 16px;
    }

    sl-dialog {
      --width: 75vw;
    }
  `];
}
